import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { Observable } from 'rxjs';

import { AuthService } from '../services/auth.service';

/**
 * AuthGuard
 *
 * Route guard for protecting authenticated routes using Angular's
 * CanActivate and CanActivateChild interfaces.
 *
 * This guard checks if the user has a valid authentication token
 * before allowing access to protected routes. If not authenticated,
 * the user is redirected to the login flow with the intended URL
 * stored for post-login redirect.
 *
 * Usage:
 * ```typescript
 * const routes: Routes = [
 *   {
 *     path: 'dashboard',
 *     component: DashboardComponent,
 *     canActivate: [AuthGuard]
 *   },
 *   {
 *     path: 'admin',
 *     component: AdminLayoutComponent,
 *     canActivateChild: [AuthGuard],
 *     children: [
 *       { path: 'users', component: UsersComponent },
 *       { path: 'settings', component: SettingsComponent }
 *     ]
 *   }
 * ];
 * ```
 *
 * Features:
 * - Synchronous authentication check for performance
 * - Stores intended URL for post-login redirect
 * - Works with both parent and child routes
 * - Integrates with AuthService
 * - Redirects to login on unauthorized access
 */
@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild {
  private authService = inject(AuthService);
  private router = inject(Router);


  /**
   * Check if route can be activated
   *
   * @param route The activated route snapshot
   * @param state The router state snapshot
   * @returns true if authenticated, UrlTree for redirect if not
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.checkAuth(state.url);
  }

  /**
   * Check if child route can be activated
   *
   * @param childRoute The child route snapshot
   * @param state The router state snapshot
   * @returns true if authenticated, UrlTree for redirect if not
   */
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.checkAuth(state.url);
  }

  /**
   * Core authentication check logic
   *
   * Checks if user has valid access token. If not authenticated,
   * stores the intended URL and redirects to login.
   *
   * @param url The requested URL
   * @returns true if authenticated, UrlTree for redirect if not
   * @private
   */
  private checkAuth(url: string): boolean | UrlTree {
    // Check if user has valid access token
    if (this.authService.hasValidAccessToken()) {
      return true;
    }

    // Store intended URL for post-login redirect
    this.authService.setIntendedUrl(url);

    return this.router.createUrlTree(['/auth/landing']);
  }
}
